import { useEffect } from 'react';
import { Volume2, Pause, Play, SkipForward, Square } from 'lucide-react';
import { IconButton } from '@hump/ui';
import { useTtsStore } from '../../stores/tts';
import { useReaderStore } from '../../stores/reader';

export function TtsPlayer() {
  const { playing, currentBlockIndex, rate, voices, selectedVoiceURI,
    play, pause, resume, stop, next, setRate, setVoice, initVoices } = useTtsStore();
  const { blocks } = useReaderStore();

  const texts = blocks.filter((b) => b.type !== 'code' && b.original.trim()).map((b) => b.original);

  // 初始化语音列表——放在 useEffect 里，避免渲染中 setState 导致无限循环
  useEffect(() => {
    if (voices.length === 0 && 'speechSynthesis' in window) {
      initVoices();
    }
  }, [voices.length, initVoices]);

  if (texts.length === 0) return null;

  function handlePlayPause() {
    if (playing) pause();
    else if (currentBlockIndex >= 0) resume();
    else play(texts);
  }

  if (!playing && currentBlockIndex < 0) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30">
        <button onClick={() => play(texts)}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-camel text-white shadow-lg hover:bg-camel-light transition-colors">
          <Volume2 className="w-4 h-4" />
          <span className="text-sm">朗读</span>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 md:bottom-4 md:left-1/2 md:-translate-x-1/2 md:rounded-xl bg-white dark:bg-warm-dark shadow-lg border-t md:border border-border-warm dark:border-border-warm-dark z-30">
      <div className="flex items-center gap-3 px-4 py-2 pb-[env(safe-area-inset-bottom)]">
        <IconButton size="sm" onClick={handlePlayPause} aria-label={playing ? '暂停' : '播放'}>
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </IconButton>
        <IconButton size="sm" onClick={() => next(texts)} disabled={!playing} aria-label="下一段">
          <SkipForward className="w-4 h-4" />
        </IconButton>
        <IconButton size="sm" onClick={stop} aria-label="停止">
          <Square className="w-4 h-4" />
        </IconButton>
        <span className="text-xs text-charcoal/50 dark:text-charcoal-light/50 flex-1">
          {playing ? `朗读中 · 第 ${currentBlockIndex + 1}/${texts.length} 段` : '已暂停'}
        </span>
        <select value={rate} onChange={(e) => setRate(Number(e.target.value))}
          className="text-xs bg-transparent text-charcoal dark:text-charcoal-light border border-border-warm dark:border-border-warm-dark rounded px-1 py-0.5">
          {[0.5, 0.75, 1, 1.25, 1.5, 2].map((r) => (<option key={r} value={r}>{r}x</option>))}
        </select>
        {voices.length > 0 && (
          <select value={selectedVoiceURI ?? ''} onChange={(e) => setVoice(e.target.value)}
            className="hidden md:block text-xs bg-transparent text-charcoal dark:text-charcoal-light border border-border-warm dark:border-border-warm-dark rounded px-1 py-0.5 max-w-32">
            <option value="">默认音色</option>
            {voices.filter((v) => v.lang.startsWith('en') || v.lang.startsWith('zh')).map((v) => (
              <option key={v.voiceURI} value={v.voiceURI}>{v.name}</option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}
